import { useState, useEffect } from 'react';
import axios from 'axios';
import { track } from '../../lib/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentModal = ({ product, onClose }) => {
  const [config, setConfig] = useState({ stripe_enabled: false, paypal_enabled: false });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { axios.get(`${API}/payments/config`).then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const pay = async (method) => {
    if (!email) { setError('Please enter your email address'); return; }
    setBusy(true); setError('');
    track('begin_checkout', { currency: 'AUD', value: Number(product.price), payment_type: method, items: [{ item_id: product.id, item_name: product.title, price: Number(product.price), quantity: 1 }] });
    try {
      const r = await axios.post(`${API}/payments/create-checkout`, {
        product_id: product.id, customer_email: email, customer_name: name, payment_method: method, origin_url: window.location.origin,
      });
      if (r.data.success && r.data.checkout_url) window.location.href = r.data.checkout_url;
      else setError('Failed to create checkout session');
    } catch (err) { setError(err.response?.data?.detail || 'Payment error. Please try again.'); }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {product && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="payment-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-[#E6DAC5] flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold text-[#1E1622]">Complete Your Purchase</h3>
              <button onClick={onClose} data-testid="payment-modal-close" className="p-2 hover:bg-[#F5EFE9] rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6">
              <div className="bg-[#F5EFE9] rounded-xl p-4 mb-6">
                {product.guide_title && <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059] mb-1">{product.guide_title}</p>}
                <h4 className="font-semibold text-[#1E1622] mb-1">{product.title}</h4>
                <p className="text-2xl font-serif font-semibold text-[#3B164C]">${product.price} AUD</p>
              </div>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#1E1622] mb-2">Email *</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="payment-email-input"
                    className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1E1622] mb-2">Name (optional)</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="payment-name-input"
                    className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none" />
                </div>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="payment-error">{error}</div>}
              <div className="space-y-3">
                {config.stripe_enabled && (
                  <button onClick={() => pay('stripe')} disabled={busy} data-testid="pay-stripe-btn"
                    className="w-full bg-[#3B164C] text-white rounded-xl py-3 font-medium flex items-center justify-center gap-2 hover:bg-[#2D1037] disabled:opacity-50">
                    {busy ? <Loader2 size={20} className="animate-spin" /> : <><CreditCard size={20} /> Pay with Card (Stripe)</>}
                  </button>
                )}
                {config.paypal_enabled && (
                  <button onClick={() => pay('paypal')} disabled={busy} data-testid="pay-paypal-btn"
                    className="w-full bg-[#0070BA] text-white rounded-xl py-3 font-medium flex items-center justify-center gap-2 hover:bg-[#005A9C] disabled:opacity-50">
                    {busy ? <Loader2 size={20} className="animate-spin" /> : 'Pay with PayPal'}
                  </button>
                )}
              </div>
              <p className="mt-4 text-xs text-center text-[#1E1622]/50">Your download links appear straight after payment and are emailed to you.<br />Secure payment powered by Stripe &amp; PayPal.</p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
